// @flow

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SURVEY_LAYERS } from '../../constants/SurveyLayers';

import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import {
  useMultipleJobs,
  usePrescriptionJob,
} from 'js/components/Prescription/PrescriptionJobContext';
import { usePrescriptionJobStrategy } from './PrescriptionJobStrategyContext';
import PrescriptionEditor from 'js/components/Prescription/PrescriptionEditor';
import { useFarm } from '../../context/AccountContext';
import {
  applyBulkIntervals,
  applyBulkSoilIntervals,
  calculateBulkIntervals,
  calculateBulkLimeJobIntervals,
  calculateBulkSoilIntervals,
  calculateClassificationIntervalAreas,
  calculateClassificationIntervals,
  calculateIntervalAreas,
  calculateIntervals,
  calculateLimeJobIntervals,
  calculateOverridesArea,
  calculateSoilIntervals,
  initializeOverrides,
} from './PrescriptionUtils';
import PrescriptionSettingsDialog from 'js/components/Prescription/Dialogs/PrescriptionSettingsDialog/PrescriptionSettingsDialog';
import PrescriptionAutoAdjustDialog from 'js/components/Prescription/Dialogs/PrescriptionAutoAdjustDialog/PrescriptionAutoAdjustDialog';
import { useSurveyContext, useSurveyLayerViewCapabilities } from '../../context/SurveyContext';
import {
  isSoilLayer,
  isYieldLayer,
  LOWEST_INTERVAL_HIGH,
  MetaTypes,
  metaTypeToInitialLayerSelection,
  PrescriptionJob,
} from './PrescriptionJob';
import { Action, useActionSnackbarContext } from '../ActionSnackbarHandler/ActionSnackbarHandler';
import { YIELD_LAYERS } from '../../constants/YieldLayer';
import { useLangFile } from '../../context/LanguageContext';
import { connect, useStore } from 'react-redux';
import {
  getLimeJobValues,
  getReferenceValues,
  setSelectedSurveyClassifications,
} from '../../reducers/SurveyReducer';
import ViewModeConstants from '../../constants/ViewModeConstants';
import { SATELLITE_LAYERS } from '../../constants/SatelliteLayers';
import {
  setAppliedInitialIntervals,
  setBulkPrescriptionFutureJob,
  setBulkPrescriptionFutureJobs,
  setBulkPrescriptionJob,
  setBulkPrescriptionJobs,
  setBulkPrescriptionPrefix,
  setCurrentJob,
  setFutureJob,
  updateFutureJob,
} from '../../reducers/PrescriptionReducer';
import MultipleActionsDialog from '../Prescription/Dialogs/MultipleActionsDialog.jsx';
import { DefaultLimePrescriptionStrategy } from './LimeJob/LimeJobStrategies/DefaultLimePrescriptionStrategy';
import { AlternativeLimePrescriptionStrategy } from './LimeJob/LimeJobStrategies/AlternativeLimePrescriptionStrategy';
import { usePrescriptionEditorContext } from './PrescriptionEditorContext';
import {
  getDemandTypeForLayer,
  isDemandLayerType,
} from '../../model/surveys/country/SurveyCountryStrategy';
import { useSurveyCountryCode } from '../../hooks/useSurveyCountryCode';
import BulkPrescriptionApplyIntervalsDialog from './Dialogs/BulkPrescriptionApplyIntervalsDialog/BulkPrescriptionApplyIntervalsDialog';
import { getFieldName } from '../../helpers/StateInterpreters';
import WebAPIUtils from '../../WebAPIUtils';
import { selectField } from '../../reducers/FieldReducer';
import { jobNameWithFieldName } from '../BulkPrescriptionView/BulkUtils';
import BulkPrescriptionReturnToOverviewDialog from './Dialogs/BulkPrescriptionReturnToOverviewDialog';
import { useBulkPrescriptionClassifications } from './useBulkPrescriptionClassifications';
import { usePrescriptionJobOrchestration } from './SeedingJob/usePrescriptionJobOrchestration';
import { Identifier } from '../../model/prescription/PrescriptionModelReference';
import { MultiLayerSeedingPrescriptionStrategyV1 } from './SeedingJob/SeedingJobStrategies/MultiLayerSeedingPrescriptionStrategyV1';
import { usePrescriptionJobInitialization } from './usePrescriptionJobInitialization';
import { prepareBulkDemandOverview } from './prescriptionDemandMode';
import { CircularProgress } from '@mui/material';
import { restoreNullValues } from './nullValueRegions';
import {
  adjustLowLimeInterval,
  advanceLowLimeAdjustment,
  getLowLimeAdjustmentTargets,
} from './LimeJob/limeIntervalAdjustment';
import { PrescriptionState } from './PrescriptionState';

const validJob = (job: PrescriptionJob) => {
  const invalidIntervals = job.intervals.filter((interval) => {
    return (
      Number.isNaN(interval.min) ||
      Number.isNaN(interval.max) ||
      !Number.isFinite(interval.min) ||
      !Number.isFinite(interval.max)
    );
  });

  return invalidIntervals.length <= 0;
};

const mapStateToProps = (store) => ({
  selectedSurveyClassificationValues: store.survey.selectedSurveyClassificationValues,
  isBulkPrescription: store.prescription.isBulkPrescription,
  bulkPrescriptionFields: store.prescription.bulkPrescription.fields,
  appliedInitialIntervals: store.prescription.bulkPrescription.appliedInitialIntervals,
  bulkPrescriptionJobs: store.prescription.bulkPrescription.currentJobs,
  bulkFuturePrescriptionJobs: store.prescription.bulkPrescription.futureJobs,
  bulkPrescriptionPrefix: store.prescription.bulkPrescription.prefix,
  currentJob: store.prescription.currentJob,
  futureJob: store.prescription.futureJob,
});

const PrescriptionEditorContainer = ({
  dispatch,
  onExit,
  onExitSafe,
  onDownloadJob,
  selectedField,
  selectedSurveyClassificationValues,
  disableSettingsDialog,
  onSaveJob,
  onDeleteFutureJob,
  isResumingJob,
  updatePrescriptionJob,
  onMergePrescriptionJobs,
  isBulkPrescription,
  bulkPrescriptionFields,
  appliedInitialIntervals,
  bulkPrescriptionJobs,
  bulkFuturePrescriptionJobs,
  bulkPrescriptionPrefix,
  currentJob,
  futureJob,
}) => {
  const farm = useFarm();
  const countryCode = useSurveyCountryCode();
  const LangFile = useLangFile();
  const { addAction } = useActionSnackbarContext();
  const { surveys, selectedSurvey } = useSurveyContext();
  const { prescriptionJob, setPrescriptionJob } = usePrescriptionJob();
  const { showCurrentJob } = useMultipleJobs();
  const [isShowingCurrentJob, setShowCurrentJob] = showCurrentJob;
  const layerCapabilities = useSurveyLayerViewCapabilities(
    prescriptionJob.layer,
    ViewModeConstants.PRESCRIPTION
  );

  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showAutoAdjustDialog, setShowAutoAdjustDialog] = useState(false);
  const [classificationReset, setClassificationReset] = useState(false);
  const [lowIntervalAdjustments, setLowIntervalAdjustments] = useState([]);
  const [initialLayerType, setInitialLayerType] = useState(prescriptionJob.layer);
  const [showLoading, setShowLoading] = useState(false);
  const [isInitializingPrescription, setIsInitializingPrescription] = useState(false);
  const [showBulkApplyIntervalsDialog, setShowBulkApplyIntervalsDialog] = useState(false);
  const [showReturnToBulkOverviewDialog, setShowReturnToBulkOverviewDialog] = useState(false);

  const firstSelectedFieldInBulkRef = useRef(isBulkPrescription ? selectedField : null);
  const { fetchDemandForBulkPrescription } = useBulkPrescriptionClassifications();
  const { restartBulkPrescription, initializePrescription, prescriptionState } =
    usePrescriptionJobOrchestration();
  const initializePrescriptionJobOnce = usePrescriptionJobInitialization(prescriptionJob);
  const [bulkPrescriptionFieldIndex, setBulkPrescriptionFieldIndex] = useState(0);
  const isPreviousButtonDisabled = bulkPrescriptionFieldIndex === 0;
  const prescriptionJobStrategy = usePrescriptionJobStrategy();
  const isNextButtonDisabled =
    prescriptionState !== PrescriptionState.BULK_PRESCRIPTION_SINGLE_FIELD_VIEW ||
    bulkPrescriptionFieldIndex === bulkPrescriptionFields.length - 1;

  const { mapsState, selectedMap, updateMapState } = usePrescriptionEditorContext();

  const store = useStore();

  const pendingLowIntervalAdjustment = lowIntervalAdjustments[0];
  const jobPendingLowIntervalAdjustment =
    pendingLowIntervalAdjustment === 'future' ? futureJob : currentJob;

  const enableNdvi =
    prescriptionJob.assets != null &&
    prescriptionJob.assets[SATELLITE_LAYERS.VITALITY_NDVI] !== null &&
    prescriptionJob.assets[SATELLITE_LAYERS.VITALITY_NDVI] !== undefined;
  const enableSurveys = Boolean(
    surveys.find((survey) => survey.fieldId === prescriptionJob.fieldId)
  );

  const defaultDataFetchForJob = useCallback(
    async (farmId, date, layer, surveys, jobId, maxPrescription, onlyCurrentBulkField = false) => {
      const prescriptionJob = store.getState().prescription.currentJob;
      const bulkPrescriptionPrefix = store.getState().prescription.bulkPrescription.prefix;

      setShowLoading(true);
      if (!isBulkPrescription || onlyCurrentBulkField) {
        const surveyForField =
          surveys.find(
            (survey) =>
              survey.fieldId === selectedField.fieldId &&
              Object.keys(survey.newImages).includes(layer)
          ) || surveys.find((survey) => survey.fieldId === selectedField.fieldId);
        const refValues = await dispatch(
          getReferenceValues(farmId, selectedField.fieldId, date, layer, surveyForField, jobId)
        );
        const updatedJob = setPrescriptionJobBasedOnReferenceValues(
          refValues,
          maxPrescription,
          onlyCurrentBulkField
        );
        if (onlyCurrentBulkField && updatedJob) {
          dispatch(setBulkPrescriptionJob(updatedJob.fieldId, updatedJob));
        }
        updateMapState('leftMap', 'multipleSelections', []);
      } else {
        const jobs = { ...bulkPrescriptionJobs };
        const surveyMap = new Map();
        let combinedValues = [];

        surveys.forEach((s) => {
          if (!surveyMap.has(s.fieldId)) {
            surveyMap.set(s.fieldId, []);
          }
          surveyMap.get(s.fieldId).push(s);
        });

        const results = await Promise.all(
          bulkPrescriptionFields.map((field) => {
            const job = jobs[field.fieldId];
            const surveysForField = surveyMap.get(job.fieldId) || [];
            const latestSurvey = surveysForField[0];

            return WebAPIUtils.getLayerValues(
              farmId,
              job.fieldId,
              job.date,
              job.layer,
              latestSurvey,
              job.jobId
            ).then((result) => ({
              fieldId: job.fieldId,
              result,
            }));
          })
        );

        results.forEach(({ fieldId, result }) => {
          jobs[fieldId] = {
            ...jobs[fieldId],
            values: result,
            jobName: jobNameWithFieldName(
              bulkPrescriptionPrefix,
              getFieldName(findFieldWithId(fieldId), LangFile)
            ),
          };

          combinedValues = combinedValues.concat(result);
        });

        bulkPrescriptionFields.forEach((bulkField) => {
          const bulkJob = jobs[bulkField.fieldId];
          const prescription = (bulkField.size * maxPrescription) / prescriptionJob.fieldSize;

          bulkJob.surveyImageId = surveys.find((s) => s.fieldId === bulkJob.fieldId)?.surveyImageId;
          bulkJob.overrides = initializeOverrides(bulkJob);
          bulkJob.overrideAreas = calculateOverridesArea(bulkJob);
          bulkJob.totalPrescription = prescription;
          bulkJob.maxPrescription = prescription;
          bulkJob.metaType = prescriptionJob.metaType;
          if (prescriptionJob.metaType === MetaTypes.SEEDING) {
            bulkJob.seedingCalculatorParams = prescriptionJob.seedingCalculatorParams;
            bulkJob.prescriptionModel = null;
          }
        });

        // Calculate intervals and interval areas for all bulk jobs
        const bulkIntervals = isSoilLayer(prescriptionJob.layer)
          ? calculateBulkSoilIntervals(jobs, prescriptionJob.fieldId)
          : calculateBulkIntervals(jobs, prescriptionJob.fieldId);
        bulkPrescriptionFields.forEach((bulkField) => {
          const bulkJob = jobs[bulkField.fieldId];
          bulkJob.intervals = isSoilLayer(prescriptionJob.layer)
            ? applyBulkSoilIntervals(bulkJob, bulkIntervals)
            : applyBulkIntervals(bulkJob, bulkIntervals);
          const jobWithAreas = calculateIntervalAreas(bulkJob);
          bulkJob.intervals = jobWithAreas.intervals;
          jobs[bulkField.fieldId] = bulkJob;
        });

        dispatch(setBulkPrescriptionJobs(jobs));
        setPrescriptionJobBasedOnReferenceValues(combinedValues, maxPrescription);
      }

      setShowLoading(false);
    },
    [
      isBulkPrescription,
      setPrescriptionJobBasedOnReferenceValues,
      bulkPrescriptionFields,
      prescriptionJob,
    ]
  );

  const findFieldWithId = (fieldId) => {
    return bulkPrescriptionFields.find((field) => field.fieldId === fieldId);
  };

  const getLimePrescriptionStrategy = () => {
    // FOR NOW COUNTRY IS HARDCODED TO Default
    const country = 'Default';

    if (country === 'Default') {
      return new DefaultLimePrescriptionStrategy();
    } else if (country === 'Alternative') {
      return new AlternativeLimePrescriptionStrategy();
    } else {
      throw new Error('Unsupported country strategy');
    }
  };

  const initializeFutureLimeJob = (
    currentValues,
    futureValues,
    maxPrescription,
    futureBulkJobs,
    sourceLayerValues
  ) => {
    const vrmSet = prescriptionJob.vrmSet;
    const futureJobVrmSet = vrmSet != null ? vrmSet : { jobIndex: 1 };
    const jobName = isBulkPrescription ? bulkPrescriptionPrefix : prescriptionJob.jobName;
    const futureJobWithValues: PrescriptionJob = {
      ...prescriptionJob,
      maxPrescription,
      jobName: isResumingJob
        ? jobName
        : jobName + ` - ${LangFile.PrescriptionEditorContainer.future}`,
      values: futureValues,
      date: prescriptionJob.date
        ? new Date(
            prescriptionJob.date.getFullYear() + 1,
            prescriptionJob.date.getMonth(),
            prescriptionJob.date.getDate()
          )
        : new Date(),
      vrmSet: futureJobVrmSet,
      originalValues: currentValues,
      layerValues: sourceLayerValues,
    };

    const futureJobWithIntervalsAndOverrides: PrescriptionJob = {
      ...futureJobWithValues,
      overrides: initializeOverrides(futureJobWithValues),
      intervals: isResumingJob
        ? futureJobWithValues.intervals
        : calculateLimeJobIntervals(futureJobWithValues.values),
    };

    if (isBulkPrescription) {
      futureJobWithIntervalsAndOverrides.intervals = calculateBulkLimeJobIntervals(futureBulkJobs);
    }

    const futureJobWithOverrideAreas: PrescriptionJob = {
      ...futureJobWithIntervalsAndOverrides,
      overrideAreas: calculateOverridesArea(futureJobWithIntervalsAndOverrides),
      originalValues: currentValues,
    };

    const newFutureJob: PrescriptionJob = calculateIntervalAreas(futureJobWithOverrideAreas);
    if (jobIndex() > 0) {
      setFutureJobAsCurrentJob(newFutureJob);
      return newFutureJob;
    }

    setFutureJobAsFutureJob(newFutureJob);
    return newFutureJob;
  };

  const getLayerValuesForAllBulkFields = (jobs) => {
    const layerValues = {};

    Object.values(jobs).forEach((job) => {
      layerValues[job.fieldId] = job.layerValues;
    });

    return layerValues;
  };

  const initializeCurrentLimePrescriptionJob = (
    humusValues,
    clayValues,
    rtValues,
    maxPrescription,
    jobs,
    futureJobs
  ) => {
    const limePrescriptionForBulkOverview = getLimePrescriptionStrategy().calculateLimePrescription(
      humusValues,
      clayValues,
      rtValues,
      Math.floor(maxPrescription / prescriptionJob.fieldSize),
      prescriptionJob.limeInfo.tolerance,
      prescriptionJob.limeInfo.efficiency
    );

    const { current, future } = limePrescriptionForBulkOverview;

    const layerValues = {
      [SURVEY_LAYERS.RT]: rtValues,
      [SURVEY_LAYERS.HUMUS]: humusValues,
      [SURVEY_LAYERS.CLAY]: clayValues,
    };

    const jobWithValues = {
      ...prescriptionJob,
      maxPrescription,
      jobName: isBulkPrescription ? bulkPrescriptionPrefix : prescriptionJob.jobName,
      values: current,
      intervals: undefined,
      layerValues: isBulkPrescription ? getLayerValuesForAllBulkFields(jobs) : layerValues,
    };

    const jobWithIntervalsAndOverrides: PrescriptionJob = {
      ...jobWithValues,
      overrides: initializeOverrides(jobWithValues),
      intervals: isBulkPrescription
        ? calculateBulkLimeJobIntervals(jobs)
        : calculateLimeJobIntervals(current),
    };

    const jobWithOverrideAreas = {
      ...jobWithIntervalsAndOverrides,
      overrideAreas: calculateOverridesArea(jobWithIntervalsAndOverrides),
    };

    const newJob: PrescriptionJob = calculateIntervalAreas(jobWithOverrideAreas);

    setPrescriptionJob(newJob, false);

    let newFutureJob = null;
    if (future === null || (isResumingJob && jobIndex() <= 0)) {
      onDeleteFutureJob();
    } else {
      newFutureJob = initializeFutureLimeJob(
        current,
        future,
        maxPrescription,
        futureJobs,
        newJob.layerValues
      );
    }

    const adjustmentTargets = getLowLimeAdjustmentTargets(
      newJob,
      newFutureJob,
      LOWEST_INTERVAL_HIGH,
      isResumingJob
    );
    setLowIntervalAdjustments(adjustmentTargets);
    if (adjustmentTargets.length > 0) setShowCurrentJob(adjustmentTargets[0] === 'current');
  };

  const jobIndex = () => {
    return prescriptionJob.vrmSet ? prescriptionJob.vrmSet.jobIndex : 0;
  };

  const initializeLimeBulkJob = (bulkJob, refValues, bulkFieldSize, maxPrescription) => {
    const [humusValues, clayValues, reactionNumbers] = refValues;
    const bulkMaxPrescription = (bulkFieldSize * maxPrescription) / prescriptionJob.fieldSize;

    const currentAndFutureValues = getLimePrescriptionStrategy().calculateLimePrescription(
      humusValues,
      clayValues,
      reactionNumbers,
      Math.floor(bulkMaxPrescription / bulkFieldSize),
      prescriptionJob.limeInfo.tolerance,
      prescriptionJob.limeInfo.efficiency
    );

    const prescription = bulkMaxPrescription;

    const newJob = {
      ...bulkJob,
      metaType: MetaTypes.LIME,
      values: currentAndFutureValues.current,
      futureValues: currentAndFutureValues.future,
      layerValues: {
        [SURVEY_LAYERS.HUMUS]: humusValues,
        [SURVEY_LAYERS.CLAY]: clayValues,
        [SURVEY_LAYERS.RT]: reactionNumbers,
      },
    };

    newJob.surveyImageId = surveys.find((s) => s.fieldId === bulkJob.fieldId)?.surveyImageId;
    newJob.overrides = initializeOverrides(newJob);
    newJob.overrideAreas = calculateOverridesArea(newJob);
    newJob.totalPrescription = prescription;
    newJob.maxPrescription = prescription;
    newJob.layer = SURVEY_LAYERS.RT;
    newJob.intervals = calculateLimeJobIntervals(newJob.values);
    const jobWithAreas = calculateIntervalAreas(newJob);
    newJob.intervals = jobWithAreas.intervals;

    return newJob;
  };

  const initializeFutureBulkJob = (bulkJob) => {
    const futureJobVrmSet = bulkJob.vrmSet != null ? bulkJob.vrmSet : { jobIndex: 1 };
    const futureJobWithValues = {
      ...bulkJob,
      jobName: bulkJob.jobName
        ? bulkJob.jobName + ` - ${LangFile.PrescriptionEditorContainer.future}`
        : bulkJob.jobName,
      values: bulkJob.futureValues,
      date: bulkJob.date
        ? new Date(bulkJob.date.getFullYear() + 1, bulkJob.date.getMonth(), bulkJob.date.getDate())
        : new Date(),
      vrmSet: futureJobVrmSet,
      originalValues: bulkJob.values,
    };

    const futureJobWithIntervals = {
      ...futureJobWithValues,
      overrides: initializeOverrides(futureJobWithValues),
      intervals: calculateLimeJobIntervals(futureJobWithValues.values),
    };

    const futureJobWithOverrideAreas = {
      ...futureJobWithIntervals,
      overrideAreas: calculateOverridesArea(futureJobWithIntervals),
    };
    return calculateIntervalAreas(futureJobWithOverrideAreas);
  };

  const fetchDataForLimeJob = async (
    farmId: number,
    fieldId: number,
    date: Date,
    survey,
    jobId: number,
    maxPrescription: number
  ) => {
    setShowLoading(true);
    if (!isBulkPrescription) {
      const fieldSurveys = surveys.filter((s) => s.fieldId === prescriptionJob.fieldId);
      const textureSurveyForJob =
        fieldSurveys.find((s) =>
          [SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.CLAY].every((layer) =>
            Object.prototype.hasOwnProperty.call(s.newImages, layer)
          )
        ) || fieldSurveys[0];
      const allLayersExistInTextureSurvey = [SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.CLAY].every(
        (layer) => Object.prototype.hasOwnProperty.call(textureSurveyForJob.newImages, layer)
      );

      let refValues;
      if (allLayersExistInTextureSurvey && textureSurveyForJob === survey) {
        refValues = await dispatch(getLimeJobValues(farmId, fieldId, date, survey, jobId));
      } else {
        const [humusValues, clayValues] = await Promise.all([
          WebAPIUtils.getLayerValues(
            farmId,
            fieldId,
            date,
            SURVEY_LAYERS.HUMUS,
            textureSurveyForJob,
            jobId
          ),
          WebAPIUtils.getLayerValues(
            farmId,
            fieldId,
            date,
            SURVEY_LAYERS.CLAY,
            textureSurveyForJob,
            jobId
          ),
        ]);
        const rtValues = await WebAPIUtils.getLayerValues(
          farmId,
          fieldId,
          date,
          SURVEY_LAYERS.RT,
          survey,
          jobId
        );
        refValues = { value: [humusValues, clayValues, rtValues] };
      }
      initializeCurrentLimePrescriptionJob(
        refValues.value[0],
        refValues.value[1],
        refValues.value[2],
        maxPrescription
      );
    } else {
      const jobs = { ...bulkPrescriptionJobs };
      const surveyMap = new Map();

      surveys.forEach((s) => {
        if (!surveyMap.has(s.fieldId)) {
          surveyMap.set(s.fieldId, []);
        }
        surveyMap.get(s.fieldId).push(s);
      });

      const results = await Promise.all(
        bulkPrescriptionFields.map((field) => {
          const job = jobs[field.fieldId];
          const surveysForField = surveyMap.get(job.fieldId) || [];
          const latestSurvey = surveysForField[0];
          const latestTextureSurvey =
            surveysForField.find((s) =>
              [SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.CLAY].every((layer) =>
                Object.prototype.hasOwnProperty.call(s.newImages, layer)
              )
            ) || latestSurvey;

          if (latestTextureSurvey === latestSurvey) {
            return WebAPIUtils.getMultipleLayerValues(
              farmId,
              job.fieldId,
              job.date,
              latestSurvey,
              [SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.CLAY, SURVEY_LAYERS.RT],
              job.jobId
            ).then((result) => ({ result, bulkField: field }));
          }

          return Promise.all([
            WebAPIUtils.getLayerValues(
              farmId,
              job.fieldId,
              job.date,
              SURVEY_LAYERS.HUMUS,
              latestTextureSurvey,
              job.jobId
            ),
            WebAPIUtils.getLayerValues(
              farmId,
              job.fieldId,
              job.date,
              SURVEY_LAYERS.CLAY,
              latestTextureSurvey,
              job.jobId
            ),
            WebAPIUtils.getLayerValues(
              farmId,
              job.fieldId,
              job.date,
              SURVEY_LAYERS.RT,
              latestSurvey,
              job.jobId
            ),
          ]).then((result) => ({ result, bulkField: field }));
        })
      );

      initializeLimeBulkOverview(results, selectedField.fieldId, maxPrescription);
    }

    setShowLoading(false);
  };

  const initializeLimeBulkOverview = (refValues, selectedFieldId, maxPrescription) => {
    const bulkFutureJobs = {};
    const jobs = { ...bulkPrescriptionJobs };

    refValues.forEach(({ result, bulkField }) => {
      setShowLoading(false);
      const bulkJob = jobs[bulkField.fieldId];
      const newBulkJob = initializeLimeBulkJob(bulkJob, result, bulkField.size, maxPrescription);
      newBulkJob.limeInfo = prescriptionJob.limeInfo;

      if (newBulkJob.futureValues) {
        bulkFutureJobs[bulkField.fieldId] = initializeFutureBulkJob(newBulkJob);
      }

      jobs[bulkField.fieldId] = newBulkJob;
    });

    dispatch(setBulkPrescriptionJobs(jobs));

    if (Object.keys(bulkFutureJobs).length > 0) {
      dispatch(setBulkPrescriptionFutureJobs(bulkFutureJobs));
    }

    initializeCurrentLimePrescriptionJob(
      jobs[selectedFieldId].layerValues[SURVEY_LAYERS.HUMUS],
      jobs[selectedFieldId].layerValues[SURVEY_LAYERS.CLAY],
      jobs[selectedFieldId].values,
      maxPrescription,
      jobs,
      bulkFutureJobs
    );
  };

  const setFutureJobAsFutureJob = (futureJob: PrescriptionJob) => {
    dispatch(setFutureJob(futureJob));
  };

  const setFutureJobAsCurrentJob = (futureJob: PrescriptionJob) => {
    setPrescriptionJob(futureJob, false);
  };

  const handleSetPrefix = (prefix: string) => {
    if (!isBulkPrescription || appliedInitialIntervals) return;

    dispatch(setBulkPrescriptionPrefix(prefix));
  };

  useEffect(() => {
    const jobsWithUpdatedNames = Object.fromEntries(
      Object.entries(bulkPrescriptionJobs).map(([id, bulkJob]) => {
        const updatedBulkJob = {
          ...bulkJob,
          jobName: jobNameWithFieldName(
            bulkPrescriptionPrefix,
            getFieldName(findFieldWithId(bulkJob.fieldId), LangFile)
          ),
        };

        return [id, updatedBulkJob];
      })
    );

    dispatch(setBulkPrescriptionJobs(jobsWithUpdatedNames));
  }, [bulkPrescriptionPrefix]);

  const initializeBulkClassifications = useCallback(
    (
      bulkPrescriptionJobs,
      classificationValuesForAllFields,
      sourceLayer,
      currentPrescriptionJob
    ) => {
      let newJob = {
        ...currentPrescriptionJob,
        jobName: bulkPrescriptionPrefix,
        intervals: [],
        classifications: classificationValuesForAllFields,
        layer: sourceLayer,
        layerType: getDemandTypeForLayer(sourceLayer, countryCode),
      };
      const bulkIntervals = calculateClassificationIntervals(newJob);
      newJob.intervals = bulkIntervals;
      newJob = calculateClassificationIntervalAreas(newJob);
      if (validJob(newJob)) {
        setPrescriptionJob(newJob, false);
      }

      const bulkJobsWithClassifications = {};

      Object.values(bulkPrescriptionJobs).forEach((bulkJob) => {
        let newBulkJob = {
          ...bulkJob,
          layerType: getDemandTypeForLayer(sourceLayer, countryCode),
          layer: sourceLayer,
          assets: getNewImagesForField(bulkJob.fieldId),
          intervals: [],
          intervalAreas: 0,
        };

        newBulkJob.intervals = calculateClassificationIntervals(newBulkJob);
        newBulkJob = calculateClassificationIntervalAreas(newBulkJob);
        bulkJobsWithClassifications[bulkJob.fieldId] = newBulkJob;
      });

      dispatch(setBulkPrescriptionJobs(bulkJobsWithClassifications));
    },
    [bulkPrescriptionPrefix, countryCode, getNewImagesForField]
  );

  const onRestartBulkPrescriptionJobs = useCallback(() => {
    dispatch(setAppliedInitialIntervals(null));
    const map = Object.values(mapsState).find((map) => map.identifier === selectedMap);
    setBulkPrescriptionFieldIndex(0);
    const overviewJobs = Object.keys(bulkPrescriptionJobs).reduce((jobs, fieldId) => {
      const job =
        fieldId === String(currentJob.fieldId) ? currentJob : bulkPrescriptionJobs[fieldId];
      jobs[fieldId] = { ...job, values: restoreNullValues(job.values) };
      return jobs;
    }, {});
    const firstBulkJob = overviewJobs[firstSelectedFieldInBulkRef.current.fieldId];

    if (map.classificationsEnabled) {
      const classifications = firstBulkJob.classifications;

      if (Object.values(overviewJobs).find((bulkJob) => bulkJob.classifications === undefined)) {
        fetchDemandForBulkPrescriptionAndSetOnJobs(map.sourceLayer);
      } else {
        const classificationValues = getClassificationValuesFromAllFields(
          Object.values(overviewJobs).map((bulkJob) => bulkJob.classifications)
        );

        const classificationValuesForAllFields = {
          ...classifications,
          values: classificationValues,
        };

        initializeBulkClassifications(
          overviewJobs,
          classificationValuesForAllFields,
          map.sourceLayer,
          firstBulkJob
        );
      }
    } else if (isLimeJob) {
      const allRefValues = [];
      Object.values(overviewJobs).forEach((bulkJob) => {
        allRefValues.push({
          result: [
            bulkJob.layerValues[SURVEY_LAYERS.HUMUS],
            bulkJob.layerValues[SURVEY_LAYERS.CLAY],
            bulkJob.layerValues[SURVEY_LAYERS.RT],
          ],
          bulkField: bulkPrescriptionFields.find(
            (bulkField) => bulkField.fieldId === bulkJob.fieldId
          ),
        });
      });

      initializeLimeBulkOverview(
        allRefValues,
        firstSelectedFieldInBulkRef.current.fieldId,
        prescriptionJob.maxPrescription
      );
    } else if (
      prescriptionJobStrategy &&
      prescriptionJobStrategy instanceof MultiLayerSeedingPrescriptionStrategyV1
    ) {
      restartBulkPrescription();
    } else {
      const valuesForBulkOverview = {};
      const resetBulkJobs = {};
      const jobs = overviewJobs;

      const bulkIntervals = isSoilLayer(firstBulkJob.layer)
        ? calculateBulkSoilIntervals(jobs, firstBulkJob.fieldId)
        : calculateBulkIntervals(jobs, firstBulkJob.fieldId);

      Object.values(overviewJobs).forEach((job) => {
        valuesForBulkOverview[job.fieldId] = { [job.layer]: job.values };
        let newBulkJob = { ...job, intervals: [], forceRecalculated: false };
        newBulkJob.intervals = isSoilLayer(newBulkJob.layer)
          ? applyBulkSoilIntervals(newBulkJob, bulkIntervals)
          : applyBulkIntervals(newBulkJob, bulkIntervals);
        newBulkJob = calculateIntervalAreas(newBulkJob);
        resetBulkJobs[job.fieldId] = newBulkJob;
      });

      const jobWithValues = {
        ...firstBulkJob,
        jobName: bulkPrescriptionPrefix,
        intervals: bulkIntervals,
        intervalAreas: 0,
        layerType: undefined,
      };

      dispatch(setBulkPrescriptionJobs(resetBulkJobs));

      updatePrescriptionJob(jobWithValues, false);
      updateMapState('leftMap', 'referenceValues', valuesForBulkOverview);

      setPrescriptionJob(jobWithValues);
      updateMapState('leftMap', 'sourceLayer', jobWithValues.layer);
    }
  }, [
    bulkPrescriptionPrefix,
    updatePrescriptionJob,
    bulkPrescriptionJobs,
    firstSelectedFieldInBulkRef,
    setBulkPrescriptionJobs,
    prescriptionJobStrategy,
    restartBulkPrescription,
  ]);

  const updateBulkJobsIntervals = useCallback(
    (templateIntervals) => {
      if (
        prescriptionState === PrescriptionState.SINGLE_FIELD_PRESCRIPTION ||
        prescriptionState === PrescriptionState.BULK_PRESCRIPTION_SINGLE_FIELD_VIEW
      ) {
        return;
      }

      const isFutureJob = !isShowingCurrentJob;
      const jobsSource = isFutureJob ? bulkFuturePrescriptionJobs : bulkPrescriptionJobs;

      if (!jobsSource) {
        return;
      }

      const updatedJobs = { ...jobsSource };

      Object.keys(updatedJobs).forEach((fieldId) => {
        const bulkJob = { ...updatedJobs[fieldId] };
        if (isDemandLayerType(bulkJob.layerType)) {
          bulkJob.intervals = templateIntervals;
          updatedJobs[fieldId] = calculateClassificationIntervalAreas(bulkJob);
        } else {
          if (prescriptionJob.metaType === MetaTypes.LIME) {
            bulkJob.intervals = templateIntervals;
          } else {
            bulkJob.intervals = isSoilLayer(prescriptionJob.layer)
              ? applyBulkSoilIntervals(bulkJob, templateIntervals)
              : applyBulkIntervals(bulkJob, templateIntervals);
          }

          const jobWithAreas = calculateIntervalAreas({
            ...bulkJob,
            overrides: bulkJob.overrides || initializeOverrides(bulkJob),
            overrideAreas: bulkJob.overrideAreas || calculateOverridesArea(bulkJob),
          });
          updatedJobs[fieldId] = jobWithAreas;
        }
      });

      if (isFutureJob) {
        dispatch(setBulkPrescriptionFutureJobs(updatedJobs));
      } else {
        dispatch(setBulkPrescriptionJobs(updatedJobs));
      }
    },
    [
      isBulkPrescription,
      appliedInitialIntervals,
      bulkPrescriptionJobs,
      bulkFuturePrescriptionJobs,
      isShowingCurrentJob,
      dispatch,
    ]
  );

  const finishLowIntervalAdjustment = () => {
    const { remainingTargets, showCurrentJob } = advanceLowLimeAdjustment(lowIntervalAdjustments);
    setLowIntervalAdjustments(remainingTargets);
    setShowCurrentJob(showCurrentJob);
  };

  const onAdjustLowInterval = (amount: number) => {
    if (!pendingLowIntervalAdjustment) return;

    const isFuture = pendingLowIntervalAdjustment === 'future';
    const job = isFuture ? futureJob : currentJob;
    if (!job) return;
    const adjustedJob = adjustLowLimeInterval(job, amount);
    if (isFuture) {
      dispatch(updateFutureJob(adjustedJob));
    } else {
      dispatch(setCurrentJob(adjustedJob, true));
    }

    if (isBulkPrescription) {
      const jobs = isFuture ? bulkFuturePrescriptionJobs : bulkPrescriptionJobs;
      const adjustedJobs = {};
      Object.entries(jobs).forEach(([fieldId, job]) => {
        const adjustedBulkJob = adjustLowLimeInterval(job, amount);
        adjustedJobs[fieldId] = calculateIntervalAreas({
          ...adjustedBulkJob,
          overrides: adjustedBulkJob.overrides || initializeOverrides(adjustedBulkJob),
          overrideAreas: adjustedBulkJob.overrideAreas || calculateOverridesArea(adjustedBulkJob),
        });
      });
      dispatch(
        isFuture
          ? setBulkPrescriptionFutureJobs(adjustedJobs)
          : setBulkPrescriptionJobs(adjustedJobs)
      );
    }

    finishLowIntervalAdjustment();
  };

  const handleUpdatePrescriptionJob = useCallback((changes, recalculate) => {
    const layer = changes.layer;
    if (layer) {
      setInitialLayerType(layer);
    }
    updatePrescriptionJob(changes, recalculate);
  });

  const changeField = useCallback(
    (newIndex) => {
      dispatch(setBulkPrescriptionJob(prescriptionJob.fieldId, { ...currentJob }));

      if (futureJob) {
        dispatch(setBulkPrescriptionFutureJob(prescriptionJob.fieldId, { ...futureJob }));
      }

      handleBulkFieldChange(newIndex);
    },
    [handleBulkFieldChange, prescriptionJob.fieldId, currentJob, futureJob]
  );

  const handleBulkFieldChange = useCallback(
    (newIndex) => {
      if (newIndex < 0 || newIndex >= bulkPrescriptionFields.length) return;
      setBulkPrescriptionFieldIndex(newIndex);
      setShowCurrentJob(true);

      const newIndexField = bulkPrescriptionFields[newIndex];
      const map = Object.values(mapsState).find((map) => map.identifier === selectedMap);

      dispatch(selectField(newIndexField));
      const futureJobForSelectedField = bulkFuturePrescriptionJobs[newIndexField.fieldId];
      if (futureJobForSelectedField) {
        dispatch(setFutureJob(futureJobForSelectedField));
      }

      const nextJob = bulkPrescriptionJobs[newIndexField.fieldId];
      const nextFieldHasYield = Boolean(nextJob.assets && nextJob.assets[YIELD_LAYERS.YIELD]);
      const isSelectedMapOnYield = isYieldLayer(map.sourceLayer);
      const mapLayer = isSelectedMapOnYield && !nextFieldHasYield ? nextJob.layer : map.sourceLayer;
      const initialLayerSelection =
        prescriptionJobStrategy?.miniatureMapSelection ||
        metaTypeToInitialLayerSelection[currentJob.metaType] ||
        [];

      if (initialLayerSelection?.length > 0) {
        updateMapState('leftMap', 'multipleSelections', initialLayerSelection);
        updateMapState('leftMap', 'referenceValues', {
          [newIndexField.fieldId]: nextJob.layerValues,
        });
      } else {
        // YieldSelector loads the yield values of the new field itself.
        const fieldReferenceValues =
          isSelectedMapOnYield && nextFieldHasYield ? {} : { [mapLayer]: nextJob.values };
        updateMapState('leftMap', 'referenceValues', {
          [newIndexField.fieldId]: fieldReferenceValues,
        });
        updateMapState('leftMap', 'multipleSelections', []);
      }

      setPrescriptionJob(nextJob, false, false);

      // Maps on yield must not keep showing the previous field's yield image.
      if (!nextFieldHasYield) {
        Object.entries(mapsState)
          .filter(([, fieldMap]) => isYieldLayer(fieldMap.sourceLayer))
          .forEach(([mapName]) => updateMapState(mapName, 'sourceLayer', nextJob.layer));
      }

      if (isDemandLayerType(nextJob.layerType) && nextJob.classifications) {
        dispatch(setSelectedSurveyClassifications(nextJob.classifications, map.sourceLayer));
      }

      updateMapState('leftMap', 'classificationsEnabled', isDemandLayerType(nextJob.layerType));
    },
    [
      dispatch,
      setShowCurrentJob,
      prescriptionJob,
      currentJob,
      futureJob,
      bulkPrescriptionPrefix,
      LangFile,
      setPrescriptionJob,
      bulkPrescriptionJobs,
      mapsState,
    ]
  );

  // When a new job is started or settings are modified.
  useEffect(() => {
    if (!prescriptionJob || prescriptionJob.maxPrescription) {
      return;
    }
    setShowSettingsDialog(true);
  }, [prescriptionJob]);

  const initializeBulkPrescription = useCallback(
    (maxPrescription, prescriptionStrategy) => {
      const metaTypeToValuesFetchAction = {
        [MetaTypes.FERTILIZING]: () =>
          defaultDataFetchForJob(
            farm.farmId,
            prescriptionJob.date,
            prescriptionJob.layer,
            surveys,
            prescriptionJob.jobId,
            maxPrescription
          ),
        [MetaTypes.SEEDING]: () => {
          if (
            prescriptionStrategy &&
            prescriptionStrategy.toReference().identifier === Identifier.MultiLayerSeedingModel
          ) {
            return initializePrescription(prescriptionStrategy);
          }

          return defaultDataFetchForJob(
            farm.farmId,
            prescriptionJob.date,
            prescriptionJob.layer,
            surveys,
            prescriptionJob.jobId,
            maxPrescription,
            isBulkPrescription && !!appliedInitialIntervals
          );
        },
        [MetaTypes.LIME]: () =>
          fetchDataForLimeJob(
            farm.farmId,
            prescriptionJob.fieldId,
            prescriptionJob.date,
            selectedSurvey,
            prescriptionJob.jobId,
            maxPrescription
          ),
        [MetaTypes.SPRAYING]: () =>
          defaultDataFetchForJob(
            farm.farmId,
            prescriptionJob.date,
            prescriptionJob.layer,
            surveys,
            prescriptionJob.jobId,
            maxPrescription
          ),
        [MetaTypes.SPOT_SPRAYING]: () =>
          defaultDataFetchForJob(
            farm.farmId,
            prescriptionJob.date,
            prescriptionJob.layer,
            surveys,
            prescriptionJob.jobId,
            maxPrescription
          ),
        [MetaTypes.LEGACY]: () =>
          defaultDataFetchForJob(
            farm.farmId,
            prescriptionJob.date,
            prescriptionJob.layer,
            surveys,
            prescriptionJob.jobId,
            maxPrescription
          ),
      };

      const dataFetchAction = metaTypeToValuesFetchAction[prescriptionJob.metaType];

      return initializePrescriptionJobOnce(
        dataFetchAction,
        maxPrescription,
        !!prescriptionStrategy,
        setIsInitializingPrescription
      ).catch((error) => {
        console.error('Failed to initialize prescription job', error);
        setShowLoading(false);
        setShowSettingsDialog(true);
      });
    },
    [
      defaultDataFetchForJob,
      fetchDataForLimeJob,
      prescriptionJob,
      initializePrescriptionJobOnce,
      isBulkPrescription,
      appliedInitialIntervals,
      initializePrescription,
    ]
  );

  const onConfirmSetAppliedInitialIntervals = useCallback(() => {
    dispatch(setAppliedInitialIntervals(prescriptionJob.intervals));
    handleBulkFieldChange(0);
  });

  const isLimeJob = useMemo(() => prescriptionJob.metaType === MetaTypes.LIME, [prescriptionJob]);

  const setPrescriptionJobBasedOnReferenceValues = useCallback(
    (referenceValues, maxPrescription, onlyCurrentBulkField = false) => {
      const prescriptionJob = store.getState().prescription.currentJob;
      const values = referenceValues;

      if (values == null) {
        // invalid choice
        setPrescriptionJob({ source: null, layer: null });
        return;
      }

      const jobWithValues: PrescriptionJob = {
        ...prescriptionJob,
        maxPrescription: maxPrescription,
        totalPrescription: onlyCurrentBulkField
          ? maxPrescription
          : prescriptionJob.totalPrescription,
        values: values,
        intervals: prescriptionJob.intervals,
      };

      if (isBulkPrescription && !onlyCurrentBulkField) {
        jobWithValues.jobName = store.getState().prescription.bulkPrescription.prefix;
      }

      const commonIntervals = () =>
        isSoilLayer(prescriptionJob.layer)
          ? calculateSoilIntervals(jobWithValues)
          : calculateIntervals(jobWithValues);

      const jobWithIntervalsAndOverrides: PrescriptionJob = {
        ...jobWithValues,
        overrides: initializeOverrides(jobWithValues),
        intervals: commonIntervals(),
      };

      const jobWithOverrideAreas = {
        ...jobWithIntervalsAndOverrides,
        overrideAreas: calculateOverridesArea(jobWithIntervalsAndOverrides),
      };

      const newJob: PrescriptionJob = calculateIntervalAreas(jobWithOverrideAreas);
      if (newJob.intervals) {
        setShowLoading(false);
      }

      if (!validJob(newJob)) {
        // this layer cannot produce a valid prescription map
        addAction(
          new Action(
            'invalid-layer',
            LangFile.PrescriptionEditorContainer.invalidValues,
            'error',
            'filled'
          )
        );
        setPrescriptionJob({ layer: null });
      }

      const jobIndex = prescriptionJob.vrmSet ? prescriptionJob.vrmSet.jobIndex : 0;
      if (!jobIndex > 0) {
        setPrescriptionJob(newJob, false);
      }
      return newJob;
    },
    [prescriptionJob]
  );

  const getClassificationValuesFromAllFields = (fieldIdWithClassification) => {
    const values = [];

    Object.values(fieldIdWithClassification).forEach((classification) => {
      values.push(classification.values);
    });

    return values.flat();
  };

  const getNewImagesForField = (fieldId) => {
    const survey = surveys.find((survey) => survey.fieldId === fieldId);

    return survey ? survey.newImages : null;
  };

  const fetchDemandForBulkPrescriptionAndSetOnJobs = (sourceLayer) => {
    const demandForAllBulkJobs = fetchDemandForBulkPrescription(sourceLayer);
    demandForAllBulkJobs.then((classifications) => {
      const updatedBulkJobs = { ...bulkPrescriptionJobs };
      Object.values(bulkPrescriptionJobs).forEach((bulkJob) => {
        const updatedBulkJob = { ...bulkJob };
        updatedBulkJob.classifications = classifications[bulkJob.fieldId].classifications;
        updatedBulkJob.surveyImageId = classifications[bulkJob.fieldId].survey.surveyImageId;
        updatedBulkJobs[bulkJob.fieldId] = updatedBulkJob;
      });

      dispatch(setBulkPrescriptionJobs(updatedBulkJobs));

      const classificationValues = getClassificationValuesFromAllFields(
        Object.values(classifications).map((classification) => classification.classifications)
      );

      const classificationValuesForAllFields = {
        ...classifications[firstSelectedFieldInBulkRef.current.fieldId].classifications,
        values: classificationValues,
      };

      initializeBulkClassifications(
        updatedBulkJobs,
        classificationValuesForAllFields,
        sourceLayer,
        prescriptionJob
      );
    });
  };

  const recalculateBulkOverview = (map, demandClassifications) => {
    if (!map.classificationsEnabled) {
      setClassificationReset(true);
      return;
    }

    const { updatedBulkJobs, combinedClassifications } = prepareBulkDemandOverview(
      bulkPrescriptionJobs,
      demandClassifications,
      firstSelectedFieldInBulkRef.current.fieldId
    );
    initializeBulkClassifications(
      updatedBulkJobs,
      combinedClassifications,
      map.sourceLayer,
      prescriptionJob
    );
  };

  useEffect(() => {
    const map = Object.values(mapsState).find((map) => map.identifier === selectedMap);
    setShowLoading(false);
    if (
      classificationReset &&
      !(isBulkPrescription && !appliedInitialIntervals) &&
      prescriptionJob.values &&
      layerCapabilities &&
      selectedSurveyClassificationValues[map.sourceLayer]
    ) {
      if (map.classificationsEnabled && !(isBulkPrescription && !appliedInitialIntervals)) {
        let newJob = {
          ...prescriptionJob,
          intervals: [],
          classifications: selectedSurveyClassificationValues[map.sourceLayer],
          layer: map.sourceLayer,
          layerType: getDemandTypeForLayer(map.sourceLayer, countryCode),
          values:
            map.referenceValues[prescriptionJob.fieldId] &&
            map.referenceValues[prescriptionJob.fieldId][map.sourceLayer],
        };
        newJob.intervals = calculateClassificationIntervals(newJob);
        newJob = calculateClassificationIntervalAreas(newJob);
        if (validJob(newJob)) {
          setPrescriptionJob(newJob, false);
        }
      } else if (
        !(isBulkPrescription && !appliedInitialIntervals) &&
        !(prescriptionJob.jobId && isDemandLayerType(prescriptionJob.layerType))
      ) {
        // Saved demand jobs are locked to demand mode (the classification toggle
        // is disabled for these jobs), so layerType/classifications must not be
        // cleared for a saved demand job.
        let newJob = {
          ...prescriptionJob,
          intervals: [],
          classifications: undefined,
          layerType: undefined,
        };
        newJob.intervals = calculateIntervals(newJob);
        newJob = calculateIntervalAreas(newJob);
        if (validJob(newJob)) {
          setPrescriptionJob(newJob, false);
        }
      }
      setClassificationReset(false);
    }
  }, [
    classificationReset,
    selectedSurveyClassificationValues,
    layerCapabilities,
    prescriptionJob,
    isBulkPrescription,
    appliedInitialIntervals,
  ]);

  // Flow that starts on disabling classifications on bulk prescription overview.
  useEffect(() => {
    const map = Object.values(mapsState).find((map) => map.identifier === selectedMap);
    setShowLoading(false);
    if (classificationReset && prescriptionJob.values && layerCapabilities) {
      if (
        !map.classificationsEnabled &&
        isBulkPrescription &&
        !appliedInitialIntervals &&
        !(prescriptionJob.jobId && isDemandLayerType(prescriptionJob.layerType))
      ) {
        // Saved demand jobs are locked to demand mode (the classification toggle
        // is disabled for these jobs), so layerType/classifications must not be
        // cleared for a saved demand job.
        const valuesForBulkOverview = {};
        const resetBulkJobs = {};
        const jobs = { ...bulkPrescriptionJobs };
        const firstBulkJob = bulkPrescriptionJobs[firstSelectedFieldInBulkRef.current.fieldId];

        const bulkIntervals = isLimeJob
          ? calculateBulkLimeJobIntervals(jobs)
          : isSoilLayer(firstBulkJob.layer)
            ? calculateBulkSoilIntervals(jobs, firstBulkJob.fieldId)
            : calculateBulkIntervals(jobs, firstBulkJob.fieldId);

        Object.values(bulkPrescriptionJobs).forEach((job) => {
          valuesForBulkOverview[job.fieldId] = { [job.layer]: job.values };
          let newBulkJob = {
            ...job,
            intervals: [],
            forceRecalculated: false,
            layerType: undefined,
            classifications: undefined,
          };
          newBulkJob.intervals = isLimeJob
            ? calculateLimeJobIntervals(newBulkJob.values)
            : isSoilLayer(newBulkJob.layer)
              ? applyBulkSoilIntervals(newBulkJob, bulkIntervals)
              : applyBulkIntervals(newBulkJob, bulkIntervals);
          newBulkJob = calculateIntervalAreas(newBulkJob);
          resetBulkJobs[job.fieldId] = newBulkJob;
        });

        const jobWithValues = {
          ...firstBulkJob,
          jobName: bulkPrescriptionPrefix,
          intervals: bulkIntervals,
          intervalAreas: 0,
          layerType: undefined,
          classifications: undefined,
        };

        dispatch(setBulkPrescriptionJobs(resetBulkJobs));

        updatePrescriptionJob(jobWithValues, false);
        updateMapState('leftMap', 'referenceValues', valuesForBulkOverview);

        setPrescriptionJob(jobWithValues);
        updateMapState('leftMap', 'sourceLayer', jobWithValues.layer);
        setClassificationReset(false);
      }
    }
  }, [
    classificationReset,
    layerCapabilities,
    prescriptionJob,
    isBulkPrescription,
    appliedInitialIntervals,
    bulkPrescriptionJobs,
    isLimeJob,
    firstSelectedFieldInBulkRef.current,
  ]);

  return (
    <Box
      style={{ overflow: 'hidden' }}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
      }}
    >
      {(showLoading || isInitializingPrescription) && !showSettingsDialog && (
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
          }}
        >
          <CircularProgress size={60} thickness={5} />
        </Box>
      )}
      <PrescriptionEditor
        onDownloadJob={onDownloadJob}
        onSetShowSettingsDialog={setShowSettingsDialog}
        onSetShowAutoAdjustDialog={setShowAutoAdjustDialog}
        enableSurveys={enableSurveys || prescriptionJob.usingJobPolygon}
        selectedField={selectedField}
        enableNdvi={enableNdvi}
        onExitSafe={onExitSafe}
        onRecalculateBulkOverview={recalculateBulkOverview}
        onDeleteFutureJob={onDeleteFutureJob}
        onSaveJob={onSaveJob}
        updatePrescriptionJob={handleUpdatePrescriptionJob}
        onMergePrescriptionJobs={onMergePrescriptionJobs}
        showSettingsDialog={showSettingsDialog}
        setFutureJobAsFutureJob={setFutureJobAsFutureJob}
        isBulkPrescription={isBulkPrescription}
        bulkPrescriptionFields={bulkPrescriptionFields}
        appliedInitialIntervals={appliedInitialIntervals}
        onSetShowBulkApplyIntervalsDialog={setShowBulkApplyIntervalsDialog}
        bulkPrescriptionJobs={bulkPrescriptionJobs}
        bulkFuturePrescriptionJobs={bulkFuturePrescriptionJobs}
        updateBulkJobsIntervals={updateBulkJobsIntervals}
        bulkPrescriptionFieldIndex={bulkPrescriptionFieldIndex}
        onBulkFieldChange={changeField}
        isPreviousButtonDisabled={isPreviousButtonDisabled}
        isNextButtonDisabled={isNextButtonDisabled}
        onShowReturnToBulkOverviewDialog={() => setShowReturnToBulkOverviewDialog(true)}
        setShowLoading={setShowLoading}
      />
      {showSettingsDialog && (
        <PrescriptionSettingsDialog
          dispatch={dispatch}
          open={showSettingsDialog}
          onExit={prescriptionJob.intervals ? () => setShowSettingsDialog(false) : onExit}
          exitEnabled={showSettingsDialog}
          onSetOpen={setShowSettingsDialog}
          disableDialog={disableSettingsDialog}
          initialLayerType={initialLayerType}
          isBulkPrescription={isBulkPrescription}
          handleSetPrefix={handleSetPrefix}
          bulkPrescriptionFields={bulkPrescriptionFields}
          surveys={surveys.filter((survey) =>
            bulkPrescriptionFields.some((field) => field.fieldId === survey.fieldId)
          )}
          initializePrescriptionJob={initializeBulkPrescription}
        />
      )}
      <PrescriptionAutoAdjustDialog
        open={showAutoAdjustDialog}
        onSetOpen={setShowAutoAdjustDialog}
        isBulkPrescription={isBulkPrescription}
        appliedInitialIntervals={appliedInitialIntervals}
        updateBulkJobsIntervals={updateBulkJobsIntervals}
        bulkPrescriptionJobs={bulkPrescriptionJobs}
      />
      <BulkPrescriptionApplyIntervalsDialog
        open={showBulkApplyIntervalsDialog}
        onSetOpen={setShowBulkApplyIntervalsDialog}
        onConfirmSetAppliedInitialIntervals={onConfirmSetAppliedInitialIntervals}
      />
      <BulkPrescriptionReturnToOverviewDialog
        open={showReturnToBulkOverviewDialog}
        closeDialog={() => setShowReturnToBulkOverviewDialog(false)}
        resetBulkPrescriptionJobs={onRestartBulkPrescriptionJobs}
      />
      <MultipleActionsDialog
        open={Boolean(pendingLowIntervalAdjustment)}
        dialogProps={{
          'data-testid': 'lime-low-rate-adjustment-dialog',
          'data-job': pendingLowIntervalAdjustment,
        }}
        title={LangFile.PrescriptionEditorContainer.limeAdjustPrescription.title}
        message={
          pendingLowIntervalAdjustment && jobPendingLowIntervalAdjustment
            ? `${jobPendingLowIntervalAdjustment.intervals[0].prescription.toFixed(2)} ${LangFile.PrescriptionEditorContainer.limeAdjustPrescription.message}`
            : ''
        }
        cancelText={LangFile.PrescriptionEditorContainer.limeAdjustPrescription.cancel}
        onCancel={finishLowIntervalAdjustment}
        primaryText={LangFile.PrescriptionEditorContainer.limeAdjustPrescription.thousand}
        onPrimary={() => onAdjustLowInterval(LOWEST_INTERVAL_HIGH)}
        secondaryText={LangFile.PrescriptionEditorContainer.limeAdjustPrescription.zero}
        onSecondary={() => onAdjustLowInterval(0)}
      />
    </Box>
  );
};

PrescriptionEditorContainer.propTypes = {
  selectedField: PropTypes.object,
  onExit: PropTypes.func,
  onExitSafe: PropTypes.func,
  onDownloadJob: PropTypes.func,
  selectedSurveyReferenceValues: PropTypes.object,
  selectedSurveyClassificationValues: PropTypes.object,
  disableSettingsDialog: PropTypes.bool,
  showCurrentJob: PropTypes.bool,
  onSaveJob: PropTypes.func,
  onDeleteFutureJob: PropTypes.func,
  isResumingJob: PropTypes.bool,
  updatePrescriptionJob: PropTypes.func,
  onMergePrescriptionJobs: PropTypes.func,
  isBulkPrescription: PropTypes.bool,
  bulkPrescriptionFields: PropTypes.array,
  appliedInitialIntervals: PropTypes.array,
  bulkPrescriptionJobs: PropTypes.object,
};

export default memo(connect(mapStateToProps)(PrescriptionEditorContainer));
